import React, { useState, useEffect } from 'react';
import { 
  Modal, Form, Input, InputNumber, Select, Upload, Switch,
  Button, Tabs, Spin, notification, Tag
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import packService from '../../../../services/packService';
import productService from '../../../../services/productService';
import config from '../../../../config';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

/**
 * Add Pack Modal Component
 */
const AddModal = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Fetch products when modal opens
  useEffect(() => {
    if (visible) {
      fetchProducts();
      // Reset form when modal opens
      form.resetFields();
      setFileList([]);
      setSelectedProducts([]);
    }
  }, [visible, form]);
  
  const fetchProducts = async () => {
    try {
      const response = await productService.getAllProducts({ is_pack: 0 });
      setProducts(response.data.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching products',
        description: error.message || 'Failed to load products'
      });
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      // Prepare form data for API request
      const formData = new FormData();
      
      // Add basic fields
      Object.keys(values).forEach(key => {
        // Skip image field as we'll handle it separately
        if (key === 'image') {
          return;
        }
        
        // Convert boolean status to number
        if (key === 'status') {
          formData.append(key, values[key] ? '1' : '0');
          return;
        }
        
        // Add field to form data
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });
      
      // Add selected products
      if (selectedProducts.length > 0) {
        selectedProducts.forEach((productId, index) => {
          formData.append(`pack_products[${index}]`, productId);
        });
      }
      
      // Add image if present
      if (fileList.length > 0) {
        formData.append('image', fileList[0].originFileObj || fileList[0]);
      }
      
      // Call API to create pack
      await packService.createPack(formData);
      
      notification.success({
        message: 'Pack created successfully',
        description: 'The new pack has been added to your inventory.'
      });
      
      // Reset form and close modal
      form.resetFields();
      setFileList([]);
      setSelectedProducts([]);
      onSuccess();
      
    } catch (error) {
      notification.error({
        message: 'Failed to create pack',
        description: error.message || 'Please check your inputs and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  // File upload handlers
  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const handleBeforeUpload = (file) => {
    // Validate file type
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      notification.error({
        message: 'Invalid file type',
        description: 'You can only upload image files!'
      });
      return Upload.LIST_IGNORE;
    }
    
    // Validate file size (2MB limit)
    const isLessThan2MB = file.size / 1024 / 1024 < 2;
    if (!isLessThan2MB) {
      notification.error({
        message: 'File too large',
        description: 'Image must be smaller than 2MB!'
      });
      return Upload.LIST_IGNORE;
    }
    
    return false; // Prevent auto upload
  };

  const handleProductSelect = (productIds) => {
    setSelectedProducts(productIds);
  };
  
  // Function to get highest sort value from packs
  const getNextSortValue = async () => {
    try {
      const response = await packService.getAllPacks({ limit: 1, sort_field: 'sort', sort_order: 'desc' });
      const packs = response.data || [];
      if (packs.length > 0) {
        return (parseInt(packs[0].sort) || 0) + 1;
      }
      return 1;
    } catch (error) {
      console.error('Error getting next sort value:', error);
      return 1;
    }
  };

  // Set default sort value when form is initialized
  useEffect(() => {
    if (visible) {
      getNextSortValue().then(nextSortValue => {
        form.setFieldsValue({ sort: nextSortValue });
      });
    }
  }, [visible, form]);
  
  return (
    <Modal
      title="Add New Pack"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          className="bg-golden-navy"
        >
          Create Pack
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: true,
            price: 0,
            cost_price: 0,
            sort: 1
          }}
        >
          <Tabs defaultActiveKey="basic">
            <TabPane tab="Basic Information" key="basic">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Item
                  name="name"
                  label="Pack Name"
                  rules={[{ required: true, message: 'Please enter pack name' }]}
                >
                  <Input placeholder="Enter pack name" />
                </Form.Item>
                
                <Form.Item
                  name="price"
                  label="Selling Price"
                  rules={[{ required: true, message: 'Please enter price' }]}
                >
                  <InputNumber 
                    min={0} 
                    step={0.01} 
                    style={{ width: '100%' }} 
                    prefix="£" 
                  />
                </Form.Item>

                <Form.Item
                  name="cost_price"
                  label="Cost Price"
                  rules={[{ required: true, message: 'Please enter cost price' }]}
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    style={{ width: '100%' }}
                    prefix="£"
                  />
                </Form.Item>
                
                <Form.Item
                  name="sort"
                  label="Sort Order"
                  rules={[{ required: true, message: 'Please enter sort order' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
                
                <Form.Item
                  name="status"
                  label="Status"
                  valuePropName="checked"
                >
                  <Switch 
                    checkedChildren="Active" 
                    unCheckedChildren="Inactive" 
                    defaultChecked 
                  />
                </Form.Item>
                
                <Form.Item
                  name="description"
                  label="Description"
                  className="col-span-2"
                  rules={[{ required: true, message: 'Please enter description' }]}
                >
                  <TextArea rows={4} placeholder="Enter pack description" />
                </Form.Item>
              </div>
            </TabPane>
            
            <TabPane tab="Pack Products" key="products">
              <div className="grid grid-cols-1 gap-4">
                <Form.Item
                  name="pack_products"
                  label="Select Products"
                  rules={[{ required: true, message: 'Please select at least one product' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select products for this pack"
                    onChange={handleProductSelect}
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    showSearch
                  >
                    {products.map(product => (
                      <Option key={product.id} value={product.id}>
                        {product.product_title} - £{parseFloat(product.product_price).toFixed(2)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                
                <div className="bg-gray-50 p-4 rounded">
                  <h4 className="font-medium mb-2">Selected Products</h4>
                  {selectedProducts.length === 0 ? (
                    <p className="text-gray-500 text-sm">No products selected yet</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedProducts.map(productId => {
                        const product = products.find(p => p.id === productId);
                        return product ? (
                          <div key={productId} className="flex justify-between items-center py-1 border-b">
                            <span>{product.product_title}</span>
                            <span className="text-gray-500">£{parseFloat(product.product_price).toFixed(2)}</span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
            </TabPane>
            
            <TabPane tab="Image" key="image">
              <div className="grid grid-cols-1 gap-4">
                <Form.Item
                  label="Pack Image"
                  name="image"
                  className="text-center"
                >
                  <Upload
                    listType="picture-card"
                    fileList={fileList}
                    onChange={handleFileChange}
                    beforeUpload={handleBeforeUpload}
                    maxCount={1}
                    accept="image/*"
                  >
                    {fileList.length === 0 && (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
                
                <div className="text-center text-sm text-gray-500">
                  <p>Recommended image size: 640x640 pixels</p>
                  <p>Maximum file size: 2MB</p>
                  <p>Supported formats: JPG, PNG, GIF</p>
                </div>
              </div>
            </TabPane>
          </Tabs>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddModal;
